import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { useDict } from "@/i18n/dict";

const StarPicker = ({ value, onChange, readOnly = false, size = 18 }) => (
  <span className="inline-flex items-center gap-0.5 text-[color:var(--mango)]">
    {[1, 2, 3, 4, 5].map((i) => (
      <button
        key={i}
        type="button"
        data-testid={readOnly ? undefined : `review-star-${i}`}
        disabled={readOnly}
        onClick={() => !readOnly && onChange(i)}
        className={`${readOnly ? "" : "cursor-pointer hover:scale-110 transition-transform"}`}
        aria-label={`${i} star${i > 1 ? "s" : ""}`}
      >
        <Star size={size} fill={i <= value ? "currentColor" : "none"} strokeWidth={1.5} />
      </button>
    ))}
  </span>
);

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString();
  } catch { return ""; }
}

export default function ReviewsSection({ slug, onAverageChange }) {
  const { user } = useAuth();
  const { lang } = useLang();
  const t = useDict(lang);
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);

  const refresh = () => api.listReviews(slug).then((rs) => {
    setReviews(rs);
    if (rs.length && onAverageChange) {
      const avg = rs.reduce((s, r) => s + r.rating, 0) / rs.length;
      onAverageChange({ avg, count: rs.length });
    }
  }).catch(() => {});

  useEffect(() => { refresh(); }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setOk(false);
    try {
      await api.postReview(slug, { rating, text });
      setText("");
      setOk(true);
      await refresh();
    } catch {
      // noop
    } finally { setBusy(false); }
  };

  return (
    <section data-testid="reviews-section" className="mt-24">
      <div className="flex items-baseline gap-4 mb-10">
        <span className="section-num">04</span>
        <h2 className="eyebrow">{t.reviews.title}</h2>
      </div>

      <div className="grid lg:grid-cols-12 gap-12">
        {/* Form */}
        <div className="lg:col-span-4">
          {user ? (
            <form data-testid="review-form" onSubmit={submit} className="border border-[color:rgba(0,16,48,0.15)] p-6 space-y-5 sticky top-28">
              <p className="eyebrow">{t.reviews.yourRating}</p>
              <StarPicker value={rating} onChange={setRating} size={24} />
              <div>
                <label className="label-line">{t.reviews.yourText}</label>
                <textarea
                  data-testid="review-text"
                  required
                  rows={4}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder={t.reviews.placeholder}
                  className="input-line"
                />
              </div>
              <button data-testid="review-submit" disabled={busy} className="btn-navy w-full justify-center">
                {busy ? "..." : t.reviews.submit}
              </button>
              {ok && <p data-testid="review-success" className="text-xs text-[color:var(--palm)] mt-2">{t.reviews.success}</p>}
            </form>
          ) : (
            <div className="border border-[color:rgba(0,16,48,0.15)] p-6">
              <p className="text-sm text-[color:var(--ink-2)] mb-4">{t.reviews.login}</p>
              <Link to="/auth" data-testid="review-login-cta" className="btn-outline">{t.nav.login}</Link>
            </div>
          )}
        </div>

        {/* List */}
        <div className="lg:col-span-8 space-y-6">
          {reviews.length === 0 ? (
            <p className="text-[color:var(--ink-3)] italic font-serif-display text-2xl">{t.reviews.noReviews}</p>
          ) : reviews.map((r) => (
            <div key={r.review_id} data-testid={`review-${r.review_id}`} className="border-b border-[color:rgba(0,16,48,0.08)] pb-6">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <StarPicker value={r.rating} onChange={() => {}} readOnly size={14} />
                  <span className="text-xs uppercase tracking-[0.18em] font-medium">{r.author_name}</span>
                </div>
                <span className="text-xs text-[color:var(--ink-3)]">{formatDate(r.created_at)}</span>
              </div>
              <p className="text-base leading-relaxed text-[color:var(--ink)]">{r.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
